import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Printer, Loader2, Phone, Mail, Globe } from "lucide-react";
import api from "@/lib/api";

const COMPANY = {
  name: "Bridge Taxi, Airport Shuttle & Tours",
  phones: "902-401-6212 · 902-440-7855",
  email: "Info@addistaxiandtour.com",
  site: "addistaxiandtour.com",
};

export default function Invoice() {
  const { bookingId } = useParams();
  const [inv, setInv] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    api.get(`/invoice/${bookingId}`)
      .then(({ data }) => { setInv(data); setState("ready"); })
      .catch(() => setState("error"));
  }, [bookingId]);

  if (state === "loading") return <div className="min-h-screen bg-white flex items-center justify-center"><Loader2 className="h-10 w-10 text-[#0b1120] animate-spin" /></div>;
  if (state === "error" || !inv) return <div className="min-h-screen bg-white flex items-center justify-center text-slate-700" data-testid="invoice-not-found">Invoice not found.</div>;

  const paid = inv.status === "paid";
  const money = (n) => `$${Number(n).toFixed(2)}`;

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4 print:bg-white print:py-0">
      <style>{`@media print { @page { margin: 14mm; } .no-print { display:none !important; } body { background:#fff; } }`}</style>

      <div className="max-w-3xl mx-auto mb-4 flex justify-end no-print">
        <button onClick={() => window.print()} data-testid="invoice-print-btn"
          className="inline-flex items-center gap-2 rounded-full bg-[#0b1120] text-[#d4af37] px-6 py-3 font-semibold hover:opacity-90 transition-opacity">
          <Printer className="h-5 w-5" /> Print / Save as PDF
        </button>
      </div>

      <div className="max-w-3xl mx-auto bg-white shadow-lg print:shadow-none" data-testid="invoice-page">
        {/* Header */}
        <div className="bg-[#0b1120] px-8 py-7 flex items-start justify-between">
          <div>
            <div className="text-[#d4af37] text-xs uppercase tracking-[0.25em]">Invoice</div>
            <div className="text-white text-2xl font-bold mt-1">{COMPANY.name}</div>
            <div className="text-slate-300 text-xs mt-3 space-y-0.5">
              <div className="flex items-center gap-1.5"><Phone className="h-3 w-3 text-[#d4af37]" /> {COMPANY.phones}</div>
              <div className="flex items-center gap-1.5"><Mail className="h-3 w-3 text-[#d4af37]" /> {COMPANY.email}</div>
              <div className="flex items-center gap-1.5"><Globe className="h-3 w-3 text-[#d4af37]" /> {COMPANY.site}</div>
              {inv.company_address && <div data-testid="invoice-address">{inv.company_address}</div>}
              {inv.company_tax_number && <div data-testid="invoice-tax">HST/GST: {inv.company_tax_number}</div>}
            </div>
          </div>
          <div className={`px-4 py-2 rounded-md text-sm font-extrabold tracking-wider border-2 ${paid ? "text-emerald-600 border-emerald-500" : "text-amber-600 border-amber-500"}`} data-testid="invoice-status">
            {paid ? "PAID" : "DRAFT · BALANCE DUE"}
          </div>
        </div>

        {/* Meta */}
        <div className="px-8 py-6 grid grid-cols-2 gap-6 border-b border-slate-200">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-1">Billed to</div>
            <div className="text-slate-900 font-semibold" data-testid="invoice-customer">{inv.customer.name}</div>
            <div className="text-slate-600 text-sm">{inv.customer.email}</div>
            <div className="text-slate-600 text-sm">{inv.customer.phone}</div>
          </div>
          <div className="text-right space-y-1 text-sm">
            <div><span className="text-slate-400">Invoice #</span> <span className="text-slate-900 font-semibold" data-testid="invoice-number">{inv.invoice_number}</span></div>
            <div><span className="text-slate-400">Reference</span> <span className="text-slate-900 font-semibold">{inv.reference}</span></div>
            <div><span className="text-slate-400">Invoice date</span> <span className="text-slate-900">{inv.invoice_date}</span></div>
            <div><span className="text-slate-400">Service date</span> <span className="text-slate-900">{inv.service_date}</span></div>
            <div><span className="text-slate-400">Payment</span> <span className="text-slate-900">{inv.payment_method}</span></div>
          </div>
        </div>

        {/* Line items */}
        <div className="px-8 py-6">
          <table className="w-full text-sm" data-testid="invoice-lines">
            <thead>
              <tr className="text-left text-slate-400 uppercase text-[11px] tracking-wider border-b border-slate-200">
                <th className="py-2 font-medium">Description / Reason for payment</th>
                <th className="py-2 font-medium">Date & time</th>
                <th className="py-2 font-medium text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {inv.line_items.map((li, i) => (
                <tr key={i} className="border-b border-slate-100 align-top">
                  <td className="py-3 text-slate-900">{li.description}{li.pickup ? <div className="text-slate-500 text-xs">Pickup: {li.pickup}</div> : null}</td>
                  <td className="py-3 text-slate-600 whitespace-nowrap">{li.when}</td>
                  <td className="py-3 text-slate-900 text-right whitespace-nowrap">{money(li.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totals */}
          <div className="mt-5 ml-auto w-full sm:w-72 space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="text-slate-900">{money(inv.subtotal)} {inv.currency}</span></div>
            {inv.discount > 0 && <div className="flex justify-between"><span className="text-emerald-600">Discount{inv.promo_code ? ` (${inv.promo_code})` : ""}</span><span className="text-emerald-600">−{money(inv.discount)}</span></div>}
            {inv.tax_amount > 0 && <div className="flex justify-between"><span className="text-slate-500">{inv.tax_label}</span><span className="text-slate-900" data-testid="invoice-tax-amount">{money(inv.tax_amount)}</span></div>}
            {inv.tips_total > 0 && <div className="flex justify-between"><span className="text-slate-500">Driver tip</span><span className="text-slate-900">{money(inv.tips_total)}</span></div>}
            <div className="flex justify-between border-t border-slate-300 pt-2 mt-1"><span className="text-slate-900 font-bold">Total</span><span className="text-slate-900 font-bold" data-testid="invoice-total">{money(inv.total)} {inv.currency}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Amount paid</span><span className="text-slate-900" data-testid="invoice-paid">{money(inv.amount_paid)}</span></div>
            <div className={`flex justify-between rounded-md px-3 py-2 mt-1 ${paid ? "bg-emerald-50" : "bg-amber-50"}`}>
              <span className={`font-bold ${paid ? "text-emerald-700" : "text-amber-700"}`}>Balance due</span>
              <span className={`font-bold ${paid ? "text-emerald-700" : "text-amber-700"}`} data-testid="invoice-balance">{money(inv.balance_due)} {inv.currency}</span>
            </div>
          </div>
        </div>

        {inv.note && (
          <div className="mx-8 mb-2 rounded-md bg-slate-50 border border-slate-200 p-4" data-testid="invoice-note">
            <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-2">Booking details &amp; notes</div>
            <p className="text-slate-700 text-sm whitespace-pre-line leading-relaxed">{inv.note}</p>
          </div>
        )}

        {/* Footer */}
        <div className="px-8 py-6 border-t border-slate-200 text-center">
          <p className="text-slate-700 font-medium">Thank you for choosing {COMPANY.name}!</p>
          <p className="text-slate-400 text-xs mt-1">Fares are in {inv.currency}. HST is shown separately where applicable. Questions? {COMPANY.phones}</p>
        </div>
      </div>
    </div>
  );
}
